// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';

export type CompanionSessionMode = 'assist' | 'talk';
export type CompanionAudioSource = {kind: 'microphone'} | {kind: 'room'; channel_id: string};
export type CompanionSessionStatus =
	| 'pending'
	| 'starting'
	| 'listening'
	| 'paused'
	| 'ending'
	| 'ended'
	| 'failed'
	| 'interrupted';
export type CompanionSpeakerRole = 'self' | 'customer';

export interface CompanionSessionResponse {
	audio_source: CompanionAudioSource;
	connection?: {endpoint: string; token: string};
	system_connection?: {endpoint: string; token: string};
	session_id: string;
	meeting_id: string;
	mode?: CompanionSessionMode;
	status: CompanionSessionStatus;
}

export interface CompanionSessionStopResponse {
	status: 'ending' | 'ended';
	summary?: string | null;
}

export interface CompanionScene {
	id: string;
	name: string;
	description?: string;
	greeting?: string;
	triggerTerms?: Array<string>;
}

export interface CompanionSceneCatalogResponse {
	scenes: Array<CompanionScene>;
	default_scene_id?: string | null;
}

export interface CompanionMemoryEntry {
	scene_id: string;
	scene_name?: string | null;
	session_id: string;
	summary: string;
	started_at: string;
}

export interface CompanionMemoryListResponse {
	entries: Array<CompanionMemoryEntry>;
}

export interface CompanionMemoryClearResponse {
	cleared: number;
}

export interface CompanionSuggestionResponse {
	suggestion_id?: string;
	kind?: string;
	suggestion: unknown;
}

export type CompanionPreferencesTone = 'natural' | 'playful' | 'calm';
export type CompanionPreferencesLength = 'brief' | 'standard';

export interface CompanionPreferences {
	tone: CompanionPreferencesTone;
	length: CompanionPreferencesLength;
	nickname: string;
	background: string;
	auto_suggest: boolean;
}

export interface CompanionSessionSummary {
	activity?: 'listening' | 'thinking' | 'speaking';
	audio_source: CompanionAudioSource;
	session_id: string;
	mode?: string;
	scene_id?: string | null;
	scene_name?: string;
	status?: CompanionSessionStatus;
	channel_id?: string;
	guild_id?: string | null;
	started_at?: string;
	ended_at?: string | null;
	last_error?: string | null;
	summary?: string | null;
}

export interface CompanionSessionListResponse {
	sessions: Array<CompanionSessionSummary>;
}

export interface CompanionSessionSegment {
	id: string;
	text: string;
	speaker: string;
	speaker_id: string;
	timestamp_ms: number;
	speaker_index?: number | null;
}

export interface CompanionBookmark {
	id: string;
	segment_id?: string;
	timestamp_ms: number;
	note: string;
}

export interface CompanionActionItem {
	id: string;
	text: string;
	assignee_speaker_id?: string | null;
	timestamp_ms: number;
	completed: boolean;
}

export type CompanionInsightKind = 'summary' | 'bookmarks' | 'action_items';

export interface CompanionInsightGenerateResponse {
	summary?: string;
	bookmarks: Array<CompanionBookmark>;
	action_items: Array<CompanionActionItem>;
}

export interface CompanionSessionDetailResponse {
	session: CompanionSessionSummary;
	roles: Record<string, string>;
	segments: Array<CompanionSessionSegment>;
	bookmarks?: Array<CompanionBookmark>;
	action_items?: Array<CompanionActionItem>;
}

export interface CompanionRecording {
	id: string;
	meeting_id: string;
	speaker: string;
	part: number;
	final: boolean;
	file_name: string;
	format: string;
	bytes: number;
	duration_ms: number;
	created_at: string;
}

export interface CompanionRecordingListResponse {
	recordings: Array<CompanionRecording>;
}

function readBodyErrorMessage(body: unknown): string | null {
	if (typeof body === 'string') return body || null;
	if (!body || typeof body !== 'object') return null;
	const record = body as Record<string, unknown>;
	for (const key of ['message', 'error', 'detail']) {
		const value = record[key];
		if (typeof value === 'string' && value) return value;
		if (value && typeof value === 'object') {
			const nestedMessage = (value as Record<string, unknown>).message;
			if (typeof nestedMessage === 'string' && nestedMessage) return nestedMessage;
		}
	}
	return null;
}

export function getCompanionCommandErrorMessage(error: unknown): string | null {
	if (error instanceof HttpError) {
		return readBodyErrorMessage(error.body) ?? error.message;
	}
	if (error instanceof Error) return error.message || null;
	return null;
}

export async function startSession(params: {
	audioSource: CompanionAudioSource;
	mode?: CompanionSessionMode;
	includeSystemAudio?: boolean;
	sceneId?: string;
}): Promise<CompanionSessionResponse> {
	const response = await http.post<CompanionSessionResponse>(Endpoints.COMPANION_SESSIONS, {
		body: {
			audio_source: params.audioSource,
			mode: params.mode ?? 'assist',
			...(params.includeSystemAudio ? {include_system_audio: true} : {}),
			scene_id: params.sceneId,
		},
	});
	return response.body;
}

export async function stopSession(sessionId: string): Promise<CompanionSessionStopResponse> {
	const response = await http.post<CompanionSessionStopResponse>(Endpoints.COMPANION_SESSION_STOP(sessionId), {
		body: {},
	});
	return response.body;
}

export async function pauseSession(sessionId: string, paused: boolean): Promise<{status: CompanionSessionStatus}> {
	const response = await http.post<{status: CompanionSessionStatus}>(
		`${Endpoints.COMPANION_SESSIONS}/${encodeURIComponent(sessionId)}/pause`,
		{body: {paused}},
	);
	return response.body;
}

export async function markSpeaker(sessionId: string, speakerId: string, role: CompanionSpeakerRole): Promise<void> {
	await http.post<{ok: true}>(Endpoints.COMPANION_SESSION_SPEAKERS(sessionId), {
		body: {speaker_id: speakerId, role},
	});
}

export async function setScene(sessionId: string, sceneId: string): Promise<string> {
	const response = await http.post<{ok: true; scene_id: string}>(Endpoints.COMPANION_SESSION_SCENE(sessionId), {
		body: {scene_id: sceneId},
	});
	return response.body.scene_id;
}

export interface CompanionSuggestRequestOptions {
	action?: string;
	question?: string;
	previousResponse?: string;
}

export async function requestSuggestion(
	sessionId: string,
	actionOrOptions?: string | CompanionSuggestRequestOptions,
): Promise<CompanionSuggestionResponse> {
	const options: CompanionSuggestRequestOptions =
		typeof actionOrOptions === 'string' ? {action: actionOrOptions} : (actionOrOptions ?? {});
	const response = await http.post<CompanionSuggestionResponse>(Endpoints.COMPANION_SESSION_SUGGEST(sessionId), {
		body: {
			...(options.action ? {action: options.action} : {}),
			...(options.question ? {question: options.question} : {}),
			...(options.previousResponse ? {previous_response: options.previousResponse} : {}),
		},
		timeoutMs: 65_000,
	});
	return response.body;
}

export async function getPreferences(): Promise<CompanionPreferences> {
	const response = await http.get<CompanionPreferences>(Endpoints.COMPANION_PREFERENCES);
	return response.body;
}

export async function savePreferences(preferences: CompanionPreferences): Promise<CompanionPreferences> {
	const response = await http.put<CompanionPreferences>(Endpoints.COMPANION_PREFERENCES, {
		body: preferences,
	});
	return response.body;
}

export async function generateSessionInsight(
	sessionId: string,
	kind: CompanionInsightKind,
): Promise<CompanionInsightGenerateResponse> {
	const response = await http.post<CompanionInsightGenerateResponse>(Endpoints.COMPANION_SESSION_INSIGHTS(sessionId), {
		body: {kind},
		timeoutMs: 65_000,
	});
	return response.body;
}

export async function toggleActionItem(
	sessionId: string,
	itemId: string,
	completed: boolean,
): Promise<CompanionActionItem> {
	const response = await http.post<{action_item: CompanionActionItem}>(
		Endpoints.COMPANION_SESSION_ACTION_ITEM(sessionId, itemId),
		{body: {completed}},
	);
	return response.body.action_item;
}

export async function deleteSession(sessionId: string): Promise<void> {
	await http.delete<{ok: true}>(Endpoints.COMPANION_SESSION(sessionId));
}

export async function listScenes(mode: CompanionSessionMode = 'assist'): Promise<CompanionSceneCatalogResponse> {
	const response = await http.get<CompanionSceneCatalogResponse>(Endpoints.COMPANION_SCENES, {query: {mode}});
	return response.body;
}

export async function listSessions(limit = 100): Promise<CompanionSessionListResponse> {
	const response = await http.get<CompanionSessionListResponse>(Endpoints.COMPANION_SESSIONS, {query: {limit}});
	return response.body;
}

export async function getSession(sessionId: string): Promise<CompanionSessionDetailResponse> {
	const response = await http.get<CompanionSessionDetailResponse>(Endpoints.COMPANION_SESSION(sessionId));
	return response.body;
}

export async function listRecordings(sessionId: string): Promise<CompanionRecordingListResponse> {
	const response = await http.get<CompanionRecordingListResponse>(Endpoints.COMPANION_SESSION_RECORDINGS(sessionId));
	return response.body;
}

export async function getRecordingAudio(sessionId: string, recordingId: string): Promise<Blob> {
	const response = await http.get<Blob>(Endpoints.COMPANION_SESSION_RECORDING_AUDIO(sessionId, recordingId), {
		parse: 'binary',
	});
	return response.body;
}

export async function previewVoice(sceneId: string): Promise<Blob> {
	const response = await http.post<Blob>('/companion/preview', {body: {scene_id: sceneId}, parse: 'binary'});
	return response.body;
}

export async function interrupt(sessionId: string): Promise<void> {
	await http.post(`${Endpoints.COMPANION_SESSIONS}/${encodeURIComponent(sessionId)}/interrupt`, {body: {}});
}

export async function listTalkMemory(): Promise<CompanionMemoryListResponse> {
	const response = await http.get<CompanionMemoryListResponse>('/companion/memory');
	return response.body;
}

export async function clearTalkMemory(sceneId: string): Promise<CompanionMemoryClearResponse> {
	const response = await http.post<CompanionMemoryClearResponse>('/companion/memory/clear', {
		body: {scene_id: sceneId},
	});
	return response.body;
}
